import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CalendarDaysIcon } from '@heroicons/react/24/solid';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, resendOtp } = useAuthStore();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    const result = await login(data);

    if (result?.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
      return;
    }

    if (result?.code === 'ACCOUNT_NOT_VERIFIED') {
      const email = result.email || data.email;
      sessionStorage.setItem('pendingEmail', email);
      try { await resendOtp({ email }); } catch { }
      navigate('/verify-otp', { state: { email, autoSent: true } });
      return;
    }

    toast.error(result?.error || 'Login failed');
  };

  return (
    <div className="w-full">
      <div className="text-left mb-8">
        <h2 className="text-3xl font-bold text-white mb-2 leading-tight">Welcome Back</h2>
        <p className="text-gray-400 font-medium tracking-tight">Sign in to your account to continue</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Email</label>
            <div className="relative">
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                })}
                type="email"
                className={`block w-full px-5 py-3.5 bg-[#1A1F26] border ${errors.email ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="Your Email"
              />
            </div>
            {errors.email && <p className="mt-1 text-xs font-semibold text-red-400 ml-1">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Password</label>
            <div className="relative">
              <input
                {...register('password', { required: 'Password is required' })}
                type={showPassword ? 'text' : 'password'}
                className={`block w-full px-5 pr-12 py-3.5 bg-[#1A1F26] border ${errors.password ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="Your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
            <div className="flex justify-end pt-0.5">
              <Link to="/forgot-password" size="sm" className="text-xs font-bold text-primary-500 hover:text-primary-400 transition-colors">
                Forgot Password?
              </Link>
            </div>
            {errors.password && <p className="mt-1 text-xs font-semibold text-red-400 ml-1">{errors.password.message}</p>}
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3.5 px-6 rounded-[1rem] shadow-[0_20px_40px_-12px_rgba(79,70,229,0.4)] text-base font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              'Sign In'
            )}
          </button>
        </div>

        <p className="text-center text-sm font-semibold text-gray-400 mt-8">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-primary-500 hover:text-primary-400 font-bold ml-1 transition-colors">
            Sign Up
          </Link>
        </p>
      </form>
    </div>
  );
};

export default Login;